import { proxyCustomElement, HTMLElement, h, Host } from '@stencil/core/internal/client';
import { d as debounce_1 } from './index4.js';
import { F as FilterService, G as GroupList, b as SortOrder, S as StringService } from './index2.js';
import './index3.js';
import { T as TranslationService } from './index6.js';
import { b as breakpoints } from './index5.js';
import { H as HeadlineAppearance, d as defineCustomElement$5 } from './roche-headline2.js';
import { T as TableSortHeadersAppearance, a as TableSortHeaderType, d as defineCustomElement$2 } from './roche-table-sort-header2.js';
import { d as defineCustomElement$4 } from './roche-icon2.js';
import { d as defineCustomElement$3 } from './roche-pipeline-table-row2.js';

const rochePipelineTableCss = ":host{display:block;margin-top:3rem;margin-top:0}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}@media (min-width: 64rem){:host{margin-top:0}}.pipeline-table{display:block;width:100%}";
const RochePipelineTableStyle0 = rochePipelineTableCss;

const headers = [
    {
        name: 'compound',
        key: 'name',
    }, {
        name: 'generic name',
        key: 'genericName',
    }, {
        name: 'trade name',
        key: 'tradeName',
    }, {
        name: 'combination',
        key: 'combination',
    }, {
        name: 'indication',
        key: 'indicationShort',
    }, {
        name: 'phase \n1 2 3 f',
        key: 'phase',
    }, {
        name: 'expected \nfiling',
        key: 'filingDate',
    },
];
const headerNames = headers.map((header) => header.name);
const RochePipelineTable$1 = /*@__PURE__*/ proxyCustomElement(class RochePipelineTable extends HTMLElement {
    updateData() {
        const filteredData = FilterService.sortElements({
            rows: this.contentParsed,
            sortBy: this.sortState.sortBy,
            sortOrder: this.sortState.sortOrder,
            defaultTracking: this.defaultTracking,
        });
        if (this.groupListBy === GroupList.ALL) {
            this.pipelineData = filteredData;
            return;
        }
        this.pipelineData = FilterService.destructureGroup(FilterService
            .groupElements(filteredData, this.groupListBy));
    }
    handleSort(event) {
        if (event instanceof CustomEvent) {
            event.stopPropagation();
        }
        this.sortState = {
            sortBy: headers[event.detail.sortBy].key,
            sortOrder: event.detail.sortOrder,
        };
        this.updateData();
    }
    handleResize() {
        if (window.innerWidth < breakpoints.medium) {
            this.headers = [headerNames[0], headerNames[1], headerNames[5]];
        }
        else {
            this.headers = headerNames;
        }
    }
    constructor() {
        super();
        this.__registerHost();
        this.sortState = { sortBy: '', sortOrder: SortOrder.DESCENDING };
        this.content = undefined;
        this.groupListBy = undefined;
        this.isSmall = false;
        this.pipelineData = [];
        this.headers = headerNames;
        this.translations = undefined;
        this.handleSort = this.handleSort.bind(this);
        this.handleResize = debounce_1.debounce(this.handleResize.bind(this), 250);
    }
    get contentParsed() {
        return typeof this.content === 'string' ? JSON.parse(this.content) : this.content;
    }
    // eslint-disable-next-line class-methods-use-this
    get defaultTracking() {
        return {
            component_name: 'roche-pipeline-table',
            event: 'sort',
        };
    }
    async componentWillLoad() {
        this.updateData();
        this.handleResize();
        this.translations = {
            groupListBy: await TranslationService.translate('group list by'),
            rocheOnly: await TranslationService.translate('roche only'),
        };
    }
    render() {
        var _a;
        return (h(Host, { key: '9d69348181fff2e2667f3950a90642c3b449ccaf' }, h("div", { key: 'cef1470679292aaf56dc335bdfe3d10336850e19', class: "pipeline-table" }, h("roche-table-sort-header", { key: 'a0a7b7d463f8d391296d8ec5f03adb618dd7d839', appearance: TableSortHeadersAppearance.PIPELINE, type: TableSortHeaderType.PIPELINE, headers: this.headers }), (_a = this.pipelineData) === null || _a === void 0 ? void 0 :
            _a.map((item, index) => (item.isHeadline
                ? h("roche-headline", { key: index, level: 6, headline: `
                  ${StringService.capitalize(this.translations.groupListBy)}: 
                  ${item.groupBy
                        ? item.groupBy
                        : `${StringService.capitalize(this.translations.rocheOnly)}`}
                  `, appearance: HeadlineAppearance.PIPELINE })
                : h("roche-pipeline-table-row", { key: `pipeline-entry-${index}`, class: 'table__row', name: item.name, "trade-name": item.tradeName, "generic-name": item.genericName, combination: item.combination, "indication-short": item.indicationShort, phase: item.phase, "filing-date": item.filingDate, description: item.description, "therapeutic-area": item.therapeuticArea, "project-type": item.projectType, "managed-by": item.managedBy, partner: item.partner, "is-small": this.isSmall }))))));
    }
    static get watchers() { return {
        "content": ["updateData"],
        "groupListBy": ["updateData"]
    }; }
    static get style() { return RochePipelineTableStyle0; }
}, [0, "roche-pipeline-table", {
        "content": [1],
        "groupListBy": [1, "group-list-by"],
        "isSmall": [4, "is-small"],
        "pipelineData": [32],
        "headers": [32],
        "translations": [32]
    }, [[0, "rocheTableSortHeader", "handleSort"], [9, "resize", "handleResize"]], {
        "content": ["updateData"],
        "groupListBy": ["updateData"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-pipeline-table", "roche-headline", "roche-icon", "roche-pipeline-table-row", "roche-table-sort-header"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-pipeline-table":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RochePipelineTable$1);
            }
            break;
        case "roche-headline":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "roche-pipeline-table-row":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "roche-table-sort-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const RochePipelineTable = RochePipelineTable$1;
const defineCustomElement = defineCustomElement$1;

export { RochePipelineTable, defineCustomElement };

//# sourceMappingURL=roche-pipeline-table.js.map